/**
 * List card component for lists page
 */
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import useUserSettings from '@/features/settings/hooks/api';
import { formatPrice } from '@/lib/price';

import type { CardList } from '@/types/lists';

import type { ListStats } from '../state/enrichedListCards';

export interface ListCardProps {
  list: CardList;
  stats: ListStats;
}

export default function ListCard({ list, stats }: ListCardProps): ReactElement {
  const { settings } = useUserSettings();
  const totalCards = stats.cardCount;
  const ownedPercentage = totalCards > 0 ? Math.round((stats.ownedCount / totalCards) * 100) : 0;
  const hasValue = stats.totalPrice > 0;

  return (
    <Link
      to={`/lists/${list.id}`}
      className={clsx(
        'bg-storm/50 hover:bg-storm/80 ring-ash/20 group hover:ring-teal/40',
        'relative flex h-36 flex-col overflow-hidden rounded-xl p-4 ring-1 transition-all',
      )}
    >
      {/* Header with icon and name */}
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={clsx(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-lg',
            list.color ? 'text-white' : 'bg-slate text-ash',
          )}
          style={list.color ? { backgroundColor: list.color } : undefined}
        >
          {list.icon || '📋'}
        </div>

        {/* Name and stats */}
        <div className="min-w-0 flex-1">
          <h3 className="text-pearl group-hover:text-teal truncate font-semibold">{list.name}</h3>
          <p className="text-ash mt-0.5 text-sm">
            {totalCards} {totalCards === 1 ? 'card' : 'cards'}
            {hasValue && <span className="text-emerald-400"> · {formatPrice(stats.totalPrice, settings.currency)}</span>}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      {totalCards > 0 && (
        <div className="mt-auto pt-3">
          <div className="bg-void/50 h-2 overflow-hidden rounded-full">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                ownedPercentage >= 100 ? 'bg-emerald-500' : ownedPercentage >= 50 ? 'bg-amber-500' : 'bg-teal',
              )}
              style={{ width: `${Math.min(ownedPercentage, 100)}%` }}
            />
          </div>
          <p className="text-ash mt-1.5 text-xs">
            {stats.ownedCount > 0 ? (
              <>
                {stats.ownedCount} of {totalCards} owned ({ownedPercentage}%)
              </>
            ) : (
              'No cards owned yet'
            )}
          </p>
        </div>
      )}

      {/* Updated date */}
      <p className="text-ash/60 absolute right-3 bottom-3 text-xs">Updated {formatDistanceToNow(list.updated_at, { addSuffix: true })}</p>
    </Link>
  );
}
